import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Droplets } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { baseTokens } from "../lib/swap";
import { useTokenList } from "../components/TokenSelect";
import { PageHead } from "./Explore";
import { Panel } from "../components/State";
import { PoolForm } from "../components/liquidity/PoolForm";
import { Positions } from "../components/liquidity/Positions";

export default function Liquidity() {
  const { config: cfg } = useApp();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "create";
  const base = useMemo(() => baseTokens(cfg), [cfg]);
  const tokens = useTokenList(base);
  const quotes = useMemo(() => base.filter((t) => ["ETH", "WETH", "USDG"].includes(t.symbol)), [base]);
  const [init, setInit] = useState({});
  const tokenParam = params.get("token");

  useEffect(() => {
    if (!tokenParam) return;
    const known = tokens.find((t) => t.address.toLowerCase() === tokenParam.toLowerCase());
    if (known) { if (init.base !== known && (!init.base || init.base.fallback)) setInit((x) => ({ ...x, base: known })); return; }
    if (init.base?.address?.toLowerCase() === tokenParam.toLowerCase()) return;
    api.get(`/chain/token/${tokenParam}`).then((r) => setInit((x) => ({ ...x, base: { address: r.data.address || tokenParam, symbol: r.data.symbol, name: r.data.name, decimals: r.data.decimals, verified: false, fallback: true } }))).catch(() => {});
  }, [tokenParam, tokens, init.base]);

  const setTab = (t) => { params.set("tab", t); setParams(params, { replace: true }); };
  const switchTo = (pair, t = "add") => { if (pair) setInit({ ...pair, key: Date.now() }); setTab(t); };

  return (
    <div className="mx-auto max-w-[1300px] px-4 lg:px-8 py-10 space-y-6" data-testid="liquidity-page">
      <PageHead kicker="Liquidity manager · Uniswap v3" title="Liquidity">
        Create Uniswap v3 pools, add liquidity and manage your position NFTs on Robinhood Chain. Every transaction is simulated, then signed in your own wallet.
      </PageHead>
      <Panel title="Liquidity manager" icon={Droplets}>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="m-4 bg-black/40 border border-white/10 flex-wrap h-auto">
            {[["create", "Create Pool"], ["add", "Add Liquidity"], ["positions", "My Positions"]].map(([v, l]) =>
              <TabsTrigger key={v} value={v} data-testid={`lp-tab-${v}`} className="data-[state=active]:bg-lime data-[state=active]:text-black">{l}</TabsTrigger>)}
          </TabsList>
          {quotes.length > 0 && <>
            <TabsContent value="create"><PoolForm key={`c-${init.key || ""}-${init.base?.address || ""}`} mode="create" cfg={cfg} tokens={tokens} quotes={quotes} init={init} onSwitch={switchTo} /></TabsContent>
            <TabsContent value="add"><PoolForm key={`a-${init.key || ""}-${init.base?.address || ""}`} mode="add" cfg={cfg} tokens={tokens} quotes={quotes} init={init} onSwitch={switchTo} /></TabsContent>
          </>}
          <TabsContent value="positions"><Positions cfg={cfg} /></TabsContent>
        </Tabs>
      </Panel>
    </div>
  );
}
